import React, { ReactNode, useState } from 'react';

import { PrimaryHeaderNavLink, StandardProps, User } from '../../types/common';
import { Branding, BrandingProps } from '../Branding';
import { Navbar } from '../Navbar';
import { Nav } from '../Navbar/Nav';
import { NavItem } from '../Navbar/NavItem';
import { Sidebar } from '../Sidebar';
import { Toggle } from '../Toggle';
import { UserMenu } from '../UserMenu';

export interface PrimaryHeaderProps extends StandardProps {
  /**
   * Active navigation item index
   */
  activeNavItemIndex?: number;

  /**
   * Logo to display in the header
   */
  branding?: BrandingProps;

  /**
   * Login links to display in user menu when user is not logged in
   */
  loginLink?: ReactNode;

  /**
   * Array of navigation links to display in the header
   */
  navigationLinks?: PrimaryHeaderNavLink[];

  /**
   * User object to display in user menu when user is logged in
   */
  user?: User;

  /**
   * Array of options to display in user menu when user is logged in
   */
  userMenuOptions?: ReactNode[];

  /**
   * Determines whether to show the multioffice tooltip
   */
  showMultiofficeTooltip?: boolean;
}

export function PrimaryHeader({
  activeNavItemIndex,
  branding,
  loginLink,
  navigationLinks,
  user,
  userMenuOptions,
  showMultiofficeTooltip,
  className,
  style,
}: PrimaryHeaderProps): JSX.Element {
  const [isToggled, setIsToggled] = useState(false);
  const handleToggle = () => setIsToggled((prevToggled) => !prevToggled);

  return (
    <Navbar type='primary' className={className} style={style}>
      <Navbar.Left>
        {navigationLinks && (
          <>
            <Sidebar
              brand={<Branding {...branding} />}
              items={
                <Nav>
                  {navigationLinks.map((navigationLink, i) => (
                    <NavItem
                      key={i}
                      href={navigationLink.href}
                      isActive={activeNavItemIndex === i}
                    >
                      {navigationLink.title}
                    </NavItem>
                  ))}
                </Nav>
              }
              isOpen={isToggled}
              onClose={() => setIsToggled(false)}
            />
            <Toggle onToggle={handleToggle} />
          </>
        )}
        <Branding {...branding} />
      </Navbar.Left>
      <Navbar.Center>
        <Nav>
          {navigationLinks?.map((navigationLink, i) => (
            <NavItem
              key={i}
              href={navigationLink.href}
              isActive={activeNavItemIndex === i}
            >
              {navigationLink.title}
            </NavItem>
          ))}
        </Nav>
      </Navbar.Center>
      {((user && userMenuOptions) || loginLink) && (
        <Navbar.Right>
          <UserMenu
            user={user}
            options={userMenuOptions}
            loginLink={loginLink}
            showMultiofficeTooltip={showMultiofficeTooltip}
          />
        </Navbar.Right>
      )}
    </Navbar>
  );
}
